import {
    Box, Button, Divider, FormControl, FormLabel, Heading, Input, SimpleGrid,
    Text, VStack, useToast
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { createPhysician, getAllFacilitiesOutside } from '../../../api';
import TitleHeader from '../../../components/TitleHeader';
import SignatureInfoModal from './SignatureInfoModal';

const STATES = [
    { label: 'Alabama', value: 'AL' }, { label: 'Alaska', value: 'AK' }, { label: 'Arizona', value: 'AZ' },
    { label: 'Arkansas', value: 'AR' }, { label: 'California', value: 'CA' }, { label: 'Colorado', value: 'CO' },
    { label: 'Connecticut', value: 'CT' }, { label: 'Delaware', value: 'DE' }, { label: 'Florida', value: 'FL' },
    { label: 'Georgia', value: 'GA' }, { label: 'Hawaii', value: 'HI' }, { label: 'Idaho', value: 'ID' },
    { label: 'Illinois', value: 'IL' }, { label: 'Indiana', value: 'IN' }, { label: 'Iowa', value: 'IA' },
    { label: 'Kansas', value: 'KS' }, { label: 'Kentucky', value: 'KY' }, { label: 'Louisiana', value: 'LA' },
    { label: 'Maine', value: 'ME' }, { label: 'Maryland', value: 'MD' }, { label: 'Massachusetts', value: 'MA' },
    { label: 'Michigan', value: 'MI' }, { label: 'Minnesota', value: 'MN' }, { label: 'Mississippi', value: 'MS' },
    { label: 'Missouri', value: 'MO' }, { label: 'Montana', value: 'MT' }, { label: 'Nebraska', value: 'NE' },
    { label: 'Nevada', value: 'NV' }, { label: 'New Hampshire', value: 'NH' }, { label: 'New Jersey', value: 'NJ' },
    { label: 'New Mexico', value: 'NM' }, { label: 'New York', value: 'NY' }, { label: 'North Carolina', value: 'NC' },
    { label: 'North Dakota', value: 'ND' }, { label: 'Ohio', value: 'OH' }, { label: 'Oklahoma', value: 'OK' },
    { label: 'Oregon', value: 'OR' }, { label: 'Pennsylvania', value: 'PA' }, { label: 'Rhode Island', value: 'RI' },
    { label: 'South Carolina', value: 'SC' }, { label: 'South Dakota', value: 'SD' }, { label: 'Tennessee', value: 'TN' },
    { label: 'Texas', value: 'TX' }, { label: 'Utah', value: 'UT' }, { label: 'Vermont', value: 'VT' },
    { label: 'Virginia', value: 'VA' }, { label: 'Washington', value: 'WA' }, { label: 'West Virginia', value: 'WV' },
    { label: 'Wisconsin', value: 'WI' }, { label: 'Wyoming', value: 'WY' }
];

const PhysicianCreatePage = () => {
    const [form, setForm] = useState({
        fullName: '',
        npiNumber: '',
        specialty: '',
        licenseNumber: '',
        expirationDate: '',
        phone: '',
        email: '',
        fax: '',
        address: '',
        city: '',
        zip: '',
        deaNumber: '',
        taxonomyCode: '',
        medicaidId: '',
        medicareId: '',
        photo: '',
        signaturePhone: '',
        credentials: '',
        affiliatedFacility: '',
        issuingState: '',
        state: ''
    });

    const [issuingStateSelect, setIssuingStateSelect] = useState(null);
    const [practiceStateSelect, setPracticeStateSelect] = useState(null);
    const [facilitySelect, setFacilitySelect] = useState(null);
    const [facilities, setFacilities] = useState([]);
    const [loading, setLoading] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        getFacilities();
    }, []);

    const getFacilities = async () => {
        try {
            const response = await getAllFacilitiesOutside();
            if (response.success) {
                setFacilities(response.data.facilities);
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to load facilities.',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const requiredFields = [
            { field: form.fullName, label: 'Full Name' },
            { field: form.npiNumber, label: 'NPI Number' },
            { field: form.email, label: 'Email' },
            { field: form.phone, label: 'Phone' },
            { field: form.licenseNumber, label: 'License Number' },
            { field: form.issuingState, label: 'Issuing State' },
            { field: form.expirationDate, label: 'Expiration Date' },
            { field: form.address, label: 'Address' },
            { field: form.city, label: 'City' },
            { field: form.state, label: 'State' },
            { field: form.zip, label: 'Zip' },
            { field: form.signaturePhone, label: 'Signature Phone' },
            { field: form.affiliatedFacility, label: 'Affiliated Facility' },
        ];

        for (let { field, label } of requiredFields) {
            if (!field || field.trim() === '') {
                toast({
                    title: 'Validation Error',
                    description: `${label} is required.`,
                    status: 'error',
                    duration: 3000,
                    isClosable: true
                });
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);

        const payload = {
            fullName: form.fullName,
            npiNumber: form.npiNumber,
            specialty: form.specialty,
            license: {
                number: form.licenseNumber,
                issuingState: form.issuingState,
                expirationDate: form.expirationDate
            },
            contact: {
                phone: form.phone,
                email: form.email,
                fax: form.fax
            },
            practiceAddress: {
                address: form.address,
                city: form.city,
                state: form.state,
                zip: form.zip
            },
            affiliatedFacility: form.affiliatedFacility,
            deaNumber: form.deaNumber,
            taxonomyCode: form.taxonomyCode,
            medicaidId: form.medicaidId,
            medicareId: form.medicareId,
            credentials: form.credentials.split(',').map(c => c.trim()),
            photo: form.photo,
            signaturePhone: form.signaturePhone
        };

        const res = await createPhysician(payload);
        if (res.success) {
            toast({ title: 'Physician created.', status: 'success', duration: 3000, isClosable: true });
            navigate('/reqs/physicians');
        } else {
            toast({
                title: 'Error.',
                description: res.message || 'Failed to create',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }

        setLoading(false);
    };

    return (
        <Box maxW="6xl" mx="auto" p={8}>
            <TitleHeader title="Create Physician" />
            <VStack spacing={6} align="stretch" mt={4}>
                <Heading size="md">Personal Info</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl isRequired><FormLabel>Full Name</FormLabel><Input name="fullName" value={form.fullName} onChange={handleChange} /></FormControl>
                    <FormControl isRequired><FormLabel>NPI Number</FormLabel><Input name="npiNumber" value={form.npiNumber} onChange={handleChange} /></FormControl>
                    <FormControl><FormLabel>Specialty</FormLabel><Input name="specialty" value={form.specialty} onChange={handleChange} /></FormControl>
                    <FormControl><FormLabel>Taxonomy Code</FormLabel><Input name="taxonomyCode" value={form.taxonomyCode} onChange={handleChange} /></FormControl>
                </SimpleGrid>

                <Divider />

                <Heading size="md">Contact Info</Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    <FormControl isRequired><FormLabel>Email</FormLabel><Input name="email" value={form.email} onChange={handleChange} /></FormControl>
                    <FormControl isRequired><FormLabel>Phone</FormLabel><Input name="phone" value={form.phone} onChange={handleChange} /></FormControl>
                    <FormControl><FormLabel>Fax</FormLabel><Input name="fax" value={form.fax} onChange={handleChange} /></FormControl>
                </SimpleGrid>

                <Divider />

                <Heading size="md">License Info</Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    <FormControl isRequired><FormLabel>License Number</FormLabel><Input name="licenseNumber" value={form.licenseNumber} onChange={handleChange} /></FormControl>
                    <FormControl isRequired><FormLabel>Issuing State</FormLabel>
                        <Select
                            options={STATES}
                            value={issuingStateSelect}
                            onChange={(opt) => {
                                setIssuingStateSelect(opt);
                                setForm(prev => ({ ...prev, issuingState: opt ? opt.value : '' }));
                            }}
                            placeholder="Select state"
                            isClearable
                        />
                    </FormControl>
                    <FormControl isRequired><FormLabel>Expiration Date</FormLabel><Input name="expirationDate" type="date" value={form.expirationDate} onChange={handleChange} /></FormControl>
                </SimpleGrid>

                <Divider />

                <Heading size="md">Practice Address</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl isRequired><FormLabel>Street Address</FormLabel><Input name="address" value={form.address} onChange={handleChange} /></FormControl>
                    <FormControl isRequired><FormLabel>City</FormLabel><Input name="city" value={form.city} onChange={handleChange} /></FormControl>
                    <FormControl isRequired><FormLabel>State</FormLabel>
                        <Select
                            options={STATES}
                            value={practiceStateSelect}
                            onChange={(opt) => {
                                setPracticeStateSelect(opt);
                                setForm(prev => ({ ...prev, state: opt ? opt.value : '' }));
                            }}
                            placeholder="Select state"
                            isClearable
                        />
                    </FormControl>
                    <FormControl isRequired><FormLabel>Zip</FormLabel><Input name="zip" value={form.zip} onChange={handleChange} /></FormControl>
                </SimpleGrid>

                <Divider />

                <Heading size="md">Affiliation & IDs</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl><FormLabel>Affiliated Facility</FormLabel>
                        <Select
                            options={facilities.map(fac => ({
                                value: fac._id,
                                label: `${fac.name} - ${fac.address?.city || 'N/A'}`
                            }))}
                            onChange={(option) => {
                                setFacilitySelect(option);
                                setForm(prev => ({ ...prev, affiliatedFacility: option ? option.value : '' }));
                            }}
                            value={facilitySelect}
                            placeholder="Select a facility"
                            isClearable
                        />
                    </FormControl>
                    <FormControl><FormLabel>DEA Number</FormLabel><Input name="deaNumber" value={form.deaNumber} onChange={handleChange} /></FormControl>
                    <FormControl><FormLabel>Medicaid ID</FormLabel><Input name="medicaidId" value={form.medicaidId} onChange={handleChange} /></FormControl>
                    <FormControl><FormLabel>Medicare ID</FormLabel><Input name="medicareId" value={form.medicareId} onChange={handleChange} /></FormControl>
                </SimpleGrid>

                <Divider />

                <Heading size="md">Digital Signature</Heading>
                <SimpleGrid spacing={6}>
                    <FormControl isRequired>
                        <FormLabel>Phone Number for Signature Link</FormLabel>
                        <Input
                            name="signaturePhone"
                            placeholder="e.g. (555) 123-4567"
                            value={form.signaturePhone}
                            onChange={handleChange}
                        />
                    </FormControl>
                </SimpleGrid>

                <Box border="1px" borderColor="gray.300" rounded="md" p={4} bg="gray.50" mt={4}>
                    <Text fontSize="sm">
                        We will send a secure link to the physician’s phone for them to complete the digital signature.
                    </Text>
                    <SignatureInfoModal />
                </Box>

                <Button onClick={handleSubmit} isLoading={loading} colorScheme="teal" alignSelf="flex-start">
                    Create Physician
                </Button>
            </VStack>
        </Box>
    );
};

export default PhysicianCreatePage;
